import styled from "@emotion/styled"
import type { PHeadingProps, PModalProps } from "@porsche-design-system/components-react"
// eslint-disable-next-line no-restricted-imports
import { PHeading, PModal } from "@porsche-design-system/components-react"
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { ModalContext } from "./ModalContext"

type ModalProps = Omit<PModalProps, "heading" | "onClose" | "onDismiss" | "open"> & {
	heading?: string // heading prop of PModal is deprecated, but we can take care of it
	headingSize?: PHeadingProps["size"]
	id: string
	onDismiss?: () => void
	width?: "auto" | "small" | "wide"
}

const fullscreen: PModalProps["fullscreen"] = {
	base: true,
	s: false,
}

export const Modal = ({ children, heading, headingSize = "lg", id, onDismiss, width, ...rest }: ModalProps) => {
	const { hideModal, isOpen } = useContext(ModalContext)
	const isModalOpen = useMemo(() => isOpen(id), [isOpen, id])

	const [isOpenState, setIsOpenState] = useState(false)
	const modalRef = useRef<HTMLElement & { componentOnReady: () => Promise<void> }>(null)

	useEffect(() => {
		// guards against a stale setIsOpenState(true) firing after the modal has already been closed again (e.g. when
		// the user dismisses the modal before componentOnReady resolves) - without this, closing right after opening
		// could make the modal flicker back open, since the pending promise below isn't otherwise cancellable
		let isCancelled = false

		async function updateModalOpenState() {
			if (isModalOpen) {
				// this might be the 1st modal rendered for the user, therefore he does not have
				// the modal chunk from pds CDN cached already, so to prevent it from suddenly appearing,
				// we wait for its initialization and open it afterward
				await modalRef.current!.componentOnReady?.()
				if (!isCancelled) {
					setIsOpenState(true)
				}
			} else {
				setIsOpenState(false)
			}
		}

		void updateModalOpenState()

		return () => {
			isCancelled = true
		}
	}, [isModalOpen])

	const onModalDismiss = useCallback(() => {
		hideModal(id)
		onDismiss?.()
	}, [hideModal, id, onDismiss])

	return (
		<StyledModal
			ref={modalRef}
			$width={width}
			open={isOpenState}
			fullscreen={fullscreen}
			onDismiss={onModalDismiss}
			{...rest}
		>
			{heading && (
				<PHeading size={headingSize} tag="h2" slot="header">
					{heading}
				</PHeading>
			)}

			{children}
		</StyledModal>
	)
}

const StyledModal = styled(PModal, { shouldForwardProp })<{ $width?: "auto" | "small" | "wide" }>`
	--p-modal-width: ${({ $width }) => ($width === "small" ? "30vw" : $width === "wide" ? "70vw" : undefined)}
`
